import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, FormControl, FormGroup, ControlLabel, Tabs, Tab, ProgressBar } from 'react-bootstrap';
import styled from 'styled-components';
import $ from 'jquery';
import moment from 'moment';
import config from '../../../../../config';

// Helper to format timestamp from backend (UTC) to local time
const formatTimestamp = (timestamp) => {
  if (!timestamp) return null;
  // Parse as UTC (backend sends UTC after converting from PST)
  return moment.utc(timestamp).local().format('YYYY-MM-DD HH:mm:ss');
};

const IgnoreProblemModal = ({ 
  actionID, 
  actionIDs, // Array of action IDs for multiple actions context
  actionType,
  actionTypeID, // Action type ID (numeric, e.g., 96 for WAIT_FILE_EMERGENCE)
  problemText, 
  escalationPlan, 
  ignoredBy, 
  ignoredOn, 
  ignoreReason: existingIgnoreReason, 
  isMuted, 
  hotFolderType, 
  problemCount, // Number of problems (for multiple actions)
  workOrder, // Work order number (for multiple actions)
  problemTypes, // Array of problem types (for multiple actions)
  onClose, 
  onIgnore, 
  onNavigateToRenderQueue, // Callback to navigate to render queue
  user 
}) => {
  const [ignoreReason, setIgnoreReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUnmuting, setIsUnmuting] = useState(false);
  const [isFixing, setIsFixing] = useState(false);
  const [activeTab, setActiveTab] = useState('mute');
  const [mutedByUserName, setMutedByUserName] = useState(null);
  
  // Action flags state
  const [actionFlags, setActionFlags] = useState({
    requested: false,
    attempted: false,
    failed: false
  });
  const [fixProgress, setFixProgress] = useState(0);
  const [fixStatusText, setFixStatusText] = useState('');
  const [fixStatusBgColor, setFixStatusBgColor] = useState('#d4edda');
  const [fixStatusTextColor, setFixStatusTextColor] = useState('#155724');
  
  const progressIntervalRef = useRef(null);
  const flagCheckIntervalRef = useRef(null);
  const autoCloseTimeoutRef = useRef(null);
  const startTimeRef = useRef(null);
  
  // Render queue association state
  const [renderQueueAssociation, setRenderQueueAssociation] = useState(null); // {queueID, jobID} or null
  
  // Determine if this is multiple actions context
  const isMultipleActions = Array.isArray(actionIDs) && actionIDs.length > 0;
  const singleActionID = actionID || (isMultipleActions ? actionIDs[0] : null);
  
  // Show tabs only for single action with EDGE_PASS hot folder type (not for multiple actions)
  const showTabs = !isMultipleActions && hotFolderType === 'EDGE_PASS';
  
  // Check if action is WAIT_FILE_EMERGENCE (type 96)
  const isWaitFileEmergence = actionType === 'WAIT_FILE_EMERGENCE' || actionType === 96 || actionTypeID === 96;
  
  // Check if user is authorized
  const isAuthorizedUser = user && user.email ? 
    (user.email.toLowerCase().includes('berkman') || user.email.toLowerCase().includes('morozov')) : false;
  
  // Check render queue association for WAIT_FILE_EMERGENCE actions
  // Only check if user is authorized
  useEffect(() => {
    if (isWaitFileEmergence && singleActionID && !isMultipleActions && isAuthorizedUser) {
      $.ajax({
        method: 'GET',
        url: `${config.apiUrl}/v1/file_system/mam/transkoder/render_queue/action_associations`,
        data: { actionID: singleActionID },
        dataType: 'json'
      })
      .done((data) => {
        if (data && data.queueID && data.jobID) {
          setRenderQueueAssociation({
            queueID: data.queueID,
            jobID: data.jobID
          });
        } else {
          setRenderQueueAssociation(null);
        }
      })
      .fail(() => {
        setRenderQueueAssociation(null);
      });
    } else {
      setRenderQueueAssociation(null);
    }
  }, [singleActionID, isWaitFileEmergence, isMultipleActions, isAuthorizedUser]);
  
  // Check action flags
  const checkActionFlags = () => {
    if (!singleActionID) return;
    
    $.ajax({
      method: 'GET',
      url: `${config.apiUrl}/v1/file_system/mam/action/${singleActionID}/manual_fix`,
      dataType: 'json'
    })
    .done((data) => {
      if (data) {
        setActionFlags({
          requested: data.requested || false,
          attempted: data.attempted || false,
          failed: data.failed || false
        });
        
        // Update UI based on flags
        updateFixUI(data.requested, data.attempted, data.failed);
      }
    })
    .fail(() => {
      // Silently fail
    });
  };
  
  // Update fix UI based on flags
  const updateFixUI = (requested, attempted, failed) => {
    if (failed) {
      setFixStatusText('A fix for this problem has failed ...');
      setFixStatusBgColor('#f8d7da');
      setFixStatusTextColor('#721c24');
      setFixProgress(100);
      if (flagCheckIntervalRef.current) {
        clearInterval(flagCheckIntervalRef.current);
        flagCheckIntervalRef.current = null;
      }
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
    } else if (attempted) {
      setFixStatusText('A fix for this problem is on the way ...');
      setFixStatusBgColor('#e2e3e5');
      setFixStatusTextColor('#383d41');
      setFixProgress(100);
      if (flagCheckIntervalRef.current) {
        clearInterval(flagCheckIntervalRef.current);
        flagCheckIntervalRef.current = null;
      }
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
    } else if (requested) {
      setFixStatusText('A fix for this problem is being initiated ...');
      setFixStatusBgColor('#d1ecf1');
      setFixStatusTextColor('#0c5460');
    } else {
      setFixStatusText('Click the button below to attempt a fix for this problem');
      setFixStatusBgColor('#d4edda');
      setFixStatusTextColor('#155724');
      setFixProgress(0);
    }
  };
  
  useEffect(() => {
    // If muted, show existing reason as read-only previous info
    if (isMuted && existingIgnoreReason) {
      setIgnoreReason('');
    }
    
    // Fetch user name if muted and we have ignoredBy
    if (isMuted && ignoredBy) {
      $.ajax({
        method: 'GET',
        url: `${config.apiUrl}/v1/file_system/users/${ignoredBy}`,
        dataType: 'json'
      })
      .done((userData) => {
        if (userData && userData.firstName && userData.lastName) {
          setMutedByUserName(`${userData.firstName} ${userData.lastName}`);
        }
      })
      .fail(() => {
        // Silently fail - just show User ID if we can't get the name
        setMutedByUserName(null);
      });
    }
    
    // Check action flags on mount (only for single action)
    if (!isMultipleActions && singleActionID) {
      checkActionFlags();
    }
    
    // Cleanup intervals on unmount
    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
      if (flagCheckIntervalRef.current) {
        clearInterval(flagCheckIntervalRef.current);
      }
      if (autoCloseTimeoutRef.current) {
        clearTimeout(autoCloseTimeoutRef.current);
      }
    };
  }, [isMuted, existingIgnoreReason, ignoredBy, isMultipleActions, singleActionID]);
  
  const minLength = 20;
  const maxLength = 4095;
  const newReasonLength = ignoreReason.length;
  const isValidNewReason = newReasonLength >= minLength && newReasonLength <= maxLength;
  
  const handleIgnore = () => {
    if (isSubmitting || isUnmuting) return;
    
    // If muted and adding extra text, preserve previous reason with who/when info
    let finalReason = ignoreReason;
    
    if (isMuted && existingIgnoreReason && ignoreReason.trim().length >= minLength) {
      // Check if existing reason already has who/when info (from previous additions)
      // If it doesn't end with a "(By ... on ...)" pattern, add the original mute info
      const hasWhoWhenPattern = /\(By .+ on \d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}\)\s*$/.test(existingIgnoreReason.trim());
      
      let previousInfo = existingIgnoreReason;
      if (!hasWhoWhenPattern) {
        // Add original mute who/when if not already present
        const originalMutedBy = mutedByUserName ? `User ID ${ignoredBy} (${mutedByUserName})` : `User ID ${ignoredBy}`;
        const originalMutedOn = formatTimestamp(ignoredOn) || 'Unknown';
        previousInfo = `${existingIgnoreReason}\n(By ${originalMutedBy} on ${originalMutedOn})`;
      }
      
      // Format: Previous mute info (with who/when if not already present), then new reason (no who/when - stored in DB columns)
      finalReason = `### Previous mute info:\n${previousInfo}\n###\n\n${ignoreReason}`;
    } else if (!isMuted && !isValidNewReason) {
      return; // New mute requires valid reason
    }
    // For new mutes, just store the reason text (who/when is in IgnoredBy/IgnoredOn columns)
    
    setIsSubmitting(true);
    
    if (isMultipleActions && actionIDs && actionIDs.length > 0) {
      // Multiple actions: call ignore endpoint for each action
      const promises = actionIDs.map(actionId => {
        return $.ajax({
          method: 'POST',
          url: `${config.apiUrl}/v1/file_system/actions/problems/${actionId}/ignore`,
          contentType: 'application/x-www-form-urlencoded; charset=UTF-8',
          data: { ignore_reason: finalReason }
        });
      });
      
      Promise.all(promises)
        .then(() => {
          setIsSubmitting(false);
          onIgnore();
        })
        .fail((error) => {
          setIsSubmitting(false);
          let errorMsg = 'Could not ignore problems!';
          if (error && error.responseText) {
            errorMsg = error.responseText.replace('ErrorMsg:', '');
          }
          alert(errorMsg);
        });
    } else if (singleActionID) {
      // Single action
      $.ajax({
        method: 'POST',
        url: `${config.apiUrl}/v1/file_system/actions/problems/${singleActionID}/ignore`,
        contentType: 'application/x-www-form-urlencoded; charset=UTF-8',
        data: { ignore_reason: finalReason }
      })
      .done(() => {
        setIsSubmitting(false);
        onIgnore();
      })
      .fail((error) => {
        setIsSubmitting(false);
        let errorMsg = 'Could not ignore problem!';
        if (error && error.responseText) {
          errorMsg = error.responseText.replace('ErrorMsg:', '');
        }
        alert(errorMsg);
      });
    }
  };
  
  const handleUnmute = () => {
    if (isSubmitting || isUnmuting || isFixing) return;
    
    setIsUnmuting(true);
    $.ajax({
      method: 'POST',
      url: `${config.apiUrl}/v1/file_system/actions/problems/${singleActionID}/unmute`,
      contentType: 'application/x-www-form-urlencoded; charset=UTF-8',
      data: {}
    })
    .done(() => {
      setIsUnmuting(false);
      onIgnore(); // Refresh the view
    })
    .fail((error) => {
      setIsUnmuting(false);
      let errorMsg = 'Could not unmute problem!';
      if (error && error.responseText) {
        errorMsg = error.responseText.replace('ErrorMsg:', '');
      }
      alert(errorMsg);
    });
  };
  
  const handleFix = () => {
    if (isSubmitting || isUnmuting || isFixing || !singleActionID) return;
    
    setIsFixing(true);
    startTimeRef.current = Date.now();
    setFixProgress(0);
    
    // Start progress counter (1% per second)
    progressIntervalRef.current = setInterval(() => {
      setFixProgress(prev => {
        const newProgress = Math.min(prev + 1, 100);
        return newProgress;
      });
    }, 1000);
    
    // Start checking flags every 5 seconds
    flagCheckIntervalRef.current = setInterval(() => {
      checkActionFlags();
    }, 5000);
    
    $.ajax({
      method: 'POST',
      url: `${config.apiUrl}/v1/file_system/actions/${singleActionID}/fix`,
      contentType: 'application/x-www-form-urlencoded; charset=UTF-8',
      data: {}
    })
    .done(() => {
      // Don't close modal - wait for flags to update
      // Check flags immediately
      setTimeout(() => {
        checkActionFlags();
      }, 1000);
    })
    .fail((error) => {
      setIsFixing(false);
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
      if (flagCheckIntervalRef.current) {
        clearInterval(flagCheckIntervalRef.current);
        flagCheckIntervalRef.current = null;
      }
      let errorMsg = 'Could not fix problem!';
      if (error && error.responseText) {
        errorMsg = error.responseText.replace('ErrorMsg:', '');
      }
      alert(errorMsg);
    });
    
    // Auto-close after 120 seconds if no completion
    autoCloseTimeoutRef.current = setTimeout(() => {
      setActionFlags(prev => {
        if (!prev.attempted && !prev.failed) {
          if (progressIntervalRef.current) {
            clearInterval(progressIntervalRef.current);
            progressIntervalRef.current = null;
          }
          if (flagCheckIntervalRef.current) {
            clearInterval(flagCheckIntervalRef.current);
            flagCheckIntervalRef.current = null;
          }
          onClose();
        }
        return prev;
      });
    }, 120000);
  };
  
  // Determine if fix button should be disabled
  const isFixButtonDisabled = isSubmitting || isUnmuting || isFixing || 
    actionFlags.requested || actionFlags.attempted || actionFlags.failed;
  
  // Update fix UI when flags change
  useEffect(() => {
    if (!isMultipleActions && singleActionID) {
      updateFixUI(actionFlags.requested, actionFlags.attempted, actionFlags.failed);
      
      // If attempted or failed, clear auto-close timeout
      if (actionFlags.attempted || actionFlags.failed) {
        if (autoCloseTimeoutRef.current) {
          clearTimeout(autoCloseTimeoutRef.current);
          autoCloseTimeoutRef.current = null;
        }
      }
    }
  }, [actionFlags.requested, actionFlags.attempted, actionFlags.failed, isMultipleActions, singleActionID]);
  
  // Determine problem text to display
  const displayProblemText = isMultipleActions 
    ? `${problemCount} actions associated with ${workOrder || 'WO'}`
    : problemText;
  
  return (
    <Modal show={true} onHide={onClose} backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Action Problem Details</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <StyledModalBody>
          <div className="problem-info">
            {displayProblemText && (
              <div className="problem-text">
                {displayProblemText}
              </div>
            )}
            {escalationPlan && (
              <div className="escalation-plan">
                <strong>Escalation:</strong> {escalationPlan}
              </div>
            )}
            {isWaitFileEmergence && renderQueueAssociation && onNavigateToRenderQueue && isAuthorizedUser && (
              <div className="render-queue-link" style={{ marginTop: '10px' }}>
                <a 
                  href="#" 
                  onClick={(e) => {
                    e.preventDefault();
                    onNavigateToRenderQueue(renderQueueAssociation.queueID, renderQueueAssociation.jobID);
                    onClose();
                  }}
                  style={{ color: '#007bff', textDecoration: 'underline', cursor: 'pointer' }}
                >
                  See in Render Queue
                </a>
              </div>
            )}
          </div>
          
          {isMuted && !isMultipleActions && (
            <div className="mute-info">
              <strong>Currently muted:</strong>
              <div className="mute-details">
                <div><strong>Muted by:</strong> User ID {ignoredBy}{mutedByUserName ? ` (${mutedByUserName})` : ''}</div>
                <div><strong>Muted on:</strong> {formatTimestamp(ignoredOn) || 'N/A'}</div>
                <div><strong>Reason:</strong></div>
                <div className="previous-reason">{existingIgnoreReason || 'N/A'}</div>
              </div>
            </div>
          )}
          
          {showTabs ? (
            <Tabs activeKey={activeTab} onSelect={(key) => setActiveTab(key)} id="problem-action-tabs">
              <Tab eventKey="mute" title="Mute the Problem">
                <FormGroup>
                  <ControlLabel>
                    {isMuted ? 'Additional reason (optional)' : 'Reason for ignoring this on the dashboard'}
                    {!isMuted && <span className="required"> *</span>}
                  </ControlLabel>
                  <FormControl
                    componentClass="textarea"
                    rows={5}
                    value={ignoreReason}
                    onChange={(e) => setIgnoreReason(e.target.value)}
                    placeholder={isMuted ? `Add additional information (${minLength}-${maxLength} characters, optional)` : `Please provide a reason (${minLength}-${maxLength} characters)`}
                    maxLength={maxLength}
                    readOnly={false}
                  />
                  <div className="char-count">
                    {newReasonLength} / {maxLength} characters
                    {newReasonLength > 0 && newReasonLength < minLength && (
                      <span className="error"> (minimum {minLength} characters required)</span>
                    )}
                  </div>
                </FormGroup>
              </Tab>
              <Tab eventKey="fix" title="Fix the Problem">
                <div className="fix-info" style={{ backgroundColor: fixStatusBgColor, color: fixStatusTextColor }}>
                  <p>{fixStatusText}</p>
                  {(actionFlags.requested || isFixing) && (
                    <div className="fix-progress">
                      <ProgressBar now={fixProgress} label={`${fixProgress}%`} />
                    </div>
                  )}
                </div>
              </Tab>
            </Tabs>
          ) : (
            <FormGroup>
              <ControlLabel>
                {isMuted ? 'Additional reason (optional)' : 'Reason for ignoring this on the dashboard'}
                {!isMuted && <span className="required"> *</span>}
              </ControlLabel>
              <FormControl
                componentClass="textarea"
                rows={5}
                value={ignoreReason}
                onChange={(e) => setIgnoreReason(e.target.value)}
                placeholder={isMuted ? `Add additional information (${minLength}-${maxLength} characters, optional)` : `Please provide a reason (${minLength}-${maxLength} characters)`}
                maxLength={maxLength}
                readOnly={false}
              />
              <div className="char-count">
                {newReasonLength} / {maxLength} characters
                {newReasonLength > 0 && newReasonLength < minLength && (
                  <span className="error"> (minimum {minLength} characters required)</span>
                )}
              </div>
            </FormGroup>
          )}
        </StyledModalBody>
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={onClose}>Close the popup</Button>
        {isMuted && !isMultipleActions && (
          <Button
            bsStyle="warning"
            onClick={handleUnmute}
            disabled={isSubmitting || isUnmuting || isFixing}
          >
            {isUnmuting ? 'Unmuting...' : 'Unmute this problem'}
          </Button>
        )}
        {showTabs && activeTab === 'fix' ? (
          <Button
            bsStyle="success"
            onClick={handleFix}
            disabled={isFixButtonDisabled}
            style={isFixButtonDisabled ? { backgroundColor: '#6c757d', borderColor: '#6c757d' } : {}}
          >
            {isFixing ? 'Fixing...' : 'Fix the Problem'}
          </Button>
        ) : (
          <Button
            bsStyle="primary"
            onClick={handleIgnore}
            disabled={(!isMuted && !isValidNewReason) || (isMuted && newReasonLength > 0 && !isValidNewReason) || isSubmitting || isUnmuting || isFixing}
          >
            {isSubmitting ? 'Saving...' : isMuted ? 'Add additional reason' : 'Ignore this on dashboard'}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

const StyledModalBody = styled.div`
  .problem-info {
    margin-bottom: 20px;
    padding: 10px;
    background-color: #f5f5f5;
    border-radius: 4px;
  }
  
  .problem-text {
    margin-bottom: 10px;
    white-space: pre-wrap;
  }
  
  .escalation-plan {
    margin-top: 10px;
    padding-top: 10px;
    border-top: 1px solid #ddd;
  }
  
  .mute-info {
    margin-bottom: 20px;
    padding: 10px;
    background-color: #fff3cd;
    border: 1px solid #ffc107;
    border-radius: 4px;
    
    .mute-details {
      margin-top: 10px;
      
      > div {
        margin-bottom: 5px;
      }
      
      .previous-reason {
        margin-top: 5px;
        padding: 8px;
        background-color: #f8f9fa;
        border: 1px solid #dee2e6;
        border-radius: 3px;
        white-space: pre-wrap;
        font-family: monospace;
        font-size: 12px;
        max-height: 150px;
        overflow-y: auto;
      }
    }
  }
  
  .required {
    color: #d9534f;
  }
  
  .char-count {
    margin-top: 5px;
    font-size: 12px;
    color: #666;
    
    .error {
      color: #d9534f;
    }
  }
  
  .fix-info {
    padding: 15px;
    border: 1px solid;
    border-radius: 4px;
    margin-top: 10px;
    
    p {
      margin: 0 0 10px 0;
    }
    
    .fix-progress {
      margin-top: 10px;
    }
  }
`;

IgnoreProblemModal.propTypes = {
  actionID: PropTypes.number,
  actionIDs: PropTypes.arrayOf(PropTypes.number),
  actionType: PropTypes.string,
  problemText: PropTypes.string,
  escalationPlan: PropTypes.string,
  ignoredBy: PropTypes.number,
  ignoredOn: PropTypes.string,
  ignoreReason: PropTypes.string,
  isMuted: PropTypes.bool,
  hotFolderType: PropTypes.string,
  problemCount: PropTypes.number,
  workOrder: PropTypes.string,
  problemTypes: PropTypes.arrayOf(PropTypes.string),
  onClose: PropTypes.func.isRequired,
  onIgnore: PropTypes.func.isRequired,
  user: PropTypes.object,
};

export default IgnoreProblemModal;
